import { PolicyScope, useFormPolicy } from '@field-policy';
/* eslint-disable max-statements */
import { Box, Button, Tabs } from '@mantine/core';
import { cloneDeep, omit, pick } from 'lodash';
import { useState } from 'react';

import { Declaration, DeclarationGoods, EnvironmentCountries, RecursivePartial } from '@e-origin/shared';

import { useSaveFinalizeDataToAllDeclarations } from '@hooks/batch';
import { useSettings } from '@hooks/useSettings';
import { classes } from '@styles';
import { isHighValueDeclaration, isVatDeferredPaymentInvalid } from '@utils';
import { buildFinalizeContext, FINALIZE_POLICY_CONFIG, FinalizeForm, useFinalizeFieldRules } from '../policy';
import { FinalizeCommodityCodes } from './CommodityCodes';
import { CommodityCodeRow } from './CommodityCodes/commodityCodeRow';
import { FinalizeCustomsTreatment } from './FinalizeCustomsTreatment';
import { FinalizeDeclarationDocuments } from './FinalizeDeclarationDocuments';
import { FinalizeGlobalParameter } from './FinalizeGlobalParameter';
import { FinalizeProcedure } from './FinalizeProcedure';
import { FinalizeTransport } from './FinalizeTransport';
import { FinalizeWriteOff } from './FinalizeWriteOff';

interface BeforeSendingBatchProps {
  declaration: Declaration;
  brokerId: string;
  onHide(): void;
  disabled: boolean;
}

// refactor: merge BeforeSendingBatch and FinalizeBatch components
export const BeforeSendingBatch: React.FC<BeforeSendingBatchProps> = (props) => {
  const { data: settings } = useSettings();

  const { policy, ctx, form } = useFormPolicy({
    ...FINALIZE_POLICY_CONFIG,
    entity: props.declaration,
    readOnly: props.disabled,
    trackChangedFields: false,
    ctx: (declaration, readOnly) =>
      buildFinalizeContext({
        declaration: declaration as Declaration,
        country: settings?.country as EnvironmentCountries,
        isForwarder: Boolean(settings?.isForwarder),
        readOnly,
      }),
  });

  return (
    <PolicyScope policy={policy} ctx={ctx} form={form}>
      <BeforeSendingBatchContent {...props} />
    </PolicyScope>
  );
};

const BeforeSendingBatchContent: React.FC<BeforeSendingBatchProps> = ({ declaration, brokerId, onHide, disabled }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { data: settings } = useSettings();

  const saveFinalizeDataToAllDeclarationsMutation = useSaveFinalizeDataToAllDeclarations();

  const { form } = useFinalizeFieldRules();

  const isExport = declaration?.generalInfo?.isExport;

  const vatDeferredInvalid =
    settings.country === EnvironmentCountries.NL &&
    isHighValueDeclaration(declaration?.generalInfo?.group) &&
    isVatDeferredPaymentInvalid(form.values.messageInfo as { methodOfPaymentVAT?: string; methodOfPayment?: string; deferredPayment?: string });

  const saveData = async (values: FinalizeForm['values']) => {
    setIsSaving(true);

    const partialDeclaration = cloneDeep(
      omit(values, ['commodityCodes', 'declarationGoods', 'customsProcedureCurrent', 'customsProcedurePrevious', 'warehouse']),
    ) as RecursivePartial<Declaration>;
    partialDeclaration.goodShipmentGlobal.customsProcedure =
      ((values.customsProcedureCurrent as string) || '') + ((values.customsProcedurePrevious as string) || '');

    const transportEquipment = partialDeclaration.transportInformation?.transportEquipment as { seals?: { identificationNumber?: string }[] };
    if (transportEquipment?.seals?.length) {
      transportEquipment.seals = transportEquipment.seals.filter((s) => !!s.identificationNumber);
    }

    if (partialDeclaration.transportInformation?.transportEquipment?.containers.length) {
      partialDeclaration.transportInformation.transportEquipment.containers = partialDeclaration.transportInformation.transportEquipment.containers.filter(
        (s) => !!s.identificationNumber,
      );
    }

    if (partialDeclaration.transportInformation?.countryOfRoutingOfConsignment?.length) {
      partialDeclaration.transportInformation.countryOfRoutingOfConsignment = partialDeclaration.transportInformation.countryOfRoutingOfConsignment.filter(
        (c) => !!c.country,
      );
    }

    const declarationGoods = values.declarationGoods as RecursivePartial<DeclarationGoods>;
    if (declarationGoods?.additionalProcedure) {
      declarationGoods.additionalProcedure = declarationGoods.additionalProcedure.filter((procedure) => procedure !== '');
    }

    // Send only the top-level sections that actually changed since the form was seeded. Diffing
    // `values` itself (not `form.values`) matters for the FinalizeProcedure cascade below, which
    // builds its own values object ahead of the setFieldValue it just triggered actually landing.
    const changedTopLevelKeys = Object.keys(form.getChangedValues(values));
    // `goodShipmentGlobal.customsProcedure` is derived from two OTHER top-level fields, so a change
    // to either one must keep `goodShipmentGlobal` even if nothing else in it changed.
    if (changedTopLevelKeys.includes('customsProcedureCurrent') || changedTopLevelKeys.includes('customsProcedurePrevious')) {
      changedTopLevelKeys.push('goodShipmentGlobal');
    }
    const prunedPartialDeclaration = pick(partialDeclaration, changedTopLevelKeys) as RecursivePartial<Declaration>;

    await saveFinalizeDataToAllDeclarationsMutation.mutateAsync({
      batchId: declaration.messageInfo.batch,
      partialDeclaration: prunedPartialDeclaration,
      partialGoodsShipmentItem: declarationGoods,
      commodityCodes: (values.commodityCodes as CommodityCodeRow[]).map((c) => ({
        ...c,
        additionalReference: (c.additionalReference || []).filter((sd) => sd.selected).map((sd) => sd.document),
      })),
    });
    setIsSaving(false);
  };

  const onSubmit = async (values: FinalizeForm['values']) => {
    await saveData(values);
    onHide();
  };

  return (
    <Box data-pw="finalize-batch-modal-content">
      <Tabs defaultValue={settings?.isForwarder ? 'customs' : 'global'} mx={15} mb={15}>
        <Tabs.List>
          {!settings?.isForwarder && (
            <>
              <Tabs.Tab value="global" data-pw="finalize-global-parameter-tab">
                Global Parameter
              </Tabs.Tab>
              <Tabs.Tab value="procedure" data-pw="finalize-procedure-tab">
                Procedure
              </Tabs.Tab>
              <Tabs.Tab value="transport" data-pw="finalize-transport-tab">
                Transport
              </Tabs.Tab>
            </>
          )}
          {settings.country === EnvironmentCountries.BE && !isExport && (
            <Tabs.Tab value="writeOff" data-pw="finalize-write-off-tab">
              Write-off
            </Tabs.Tab>
          )}
          <Tabs.Tab value="customs" data-pw="finalize-customs-treatment-tab">
            Customs Treatment
          </Tabs.Tab>
          {!settings?.isForwarder && (
            <Tabs.Tab value="documents" data-pw="finalize-documents-tab">
              Documents
            </Tabs.Tab>
          )}
          <Tabs.Tab value="commodity" data-pw="finalize-commodity-codes-tab">
            Commodity code
          </Tabs.Tab>
        </Tabs.List>

        {!settings?.isForwarder && (
          <>
            <Tabs.Panel value="global" py={15} px={50} data-pw="finalize-documents-global-parameter-container">
              <FinalizeGlobalParameter disabled={disabled} brokerId={brokerId} />
            </Tabs.Panel>

            <Tabs.Panel value="procedure" py={15} px={50} data-pw="finalize-documents-procedure-container">
              <FinalizeProcedure
                disabled={disabled}
                onSave={(newGroup) => saveData({ ...form.values, generalInfo: { ...form.values.generalInfo, group: newGroup } })}
              />
            </Tabs.Panel>

            <Tabs.Panel value="transport" py={15} px={50} data-pw="finalize-transport-container">
              <FinalizeTransport disabled={disabled} />
            </Tabs.Panel>
            <Tabs.Panel value="documents" py={15} px={50} data-pw="finalize-documents-container">
              <FinalizeDeclarationDocuments declaration={declaration} disabled={disabled} />
            </Tabs.Panel>
          </>
        )}
        {settings.country === EnvironmentCountries.BE && !isExport && (
          <Tabs.Panel value="writeOff" py={15} px={50}>
            <FinalizeWriteOff declaration={declaration} disabled={disabled} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="customs" py={15} px={50} data-pw="finalize-customs-treatment-container">
          <FinalizeCustomsTreatment declaration={declaration} disabled={disabled} />
        </Tabs.Panel>

        <Tabs.Panel value="commodity" py={15} px={50}>
          <FinalizeCommodityCodes declaration={declaration} disabled={disabled} />
        </Tabs.Panel>
      </Tabs>
      <Box className={classes.footer}>
        <Button type="button" onClick={onHide} size="lg" variant="transparent">
          Cancel
        </Button>
        {!disabled && (
          <Button
            data-pw="submit-btn"
            type="button"
            onClick={() => onSubmit(form.values)}
            loading={isSaving}
            disabled={vatDeferredInvalid}
            variant="filled"
            size="lg"
          >
            Save
          </Button>
        )}
      </Box>
    </Box>
  );
};
